import axios from 'axios';

/**
 * GSWA geology via SLIP (Geology and Soils Map MapServer).
 *
 * The app already draws the 100k/250k/bedrock geology as raster WMS base maps,
 * but those are flat images - you can see the colours and not know what any of
 * them are. This adds the two things the rasters can't give you:
 *
 *   1. identify-on-click, so a click returns the actual rock unit
 *   2. faults / shear zones as vector lines
 *
 * Structure is what controls lode gold in the Yilgarn, so the fault layer is
 * arguably worth more than the unit polygons.
 *
 * Source: DEMIRS (formerly DMIRS) / Geological Survey of Western Australia.
 * The service speaks GeoJSON natively (f=geojson), so no esriJSON conversion.
 */

const BASE =
  'https://public-services.slip.wa.gov.au/public/rest/services/SLIP_Public_Services/Geology_and_Soils_Map/MapServer';

export const WA_GEOLOGY_ATTRIBUTION =
  '© State of Western Australia (DEMIRS) — Geological Survey of WA';

/** Layer ids on the SLIP Geology and Soils MapServer. */
export const WA_GEOLOGY_LAYERS = {
  bedrock100k: 21,   // 1:100 000 Interpreted Bedrock Geology (DMIRS-038)
  bedrock500k: 4,    // 1:500 000 State interpreted bedrock geology (DMIRS-016)
  faults100k: 23,    // Linear structures 1:100 000 (DMIRS-053)
  faults500k: 1,     // 1:500 000 State linear structures (DMIRS-015)
};

const client = axios.create({ baseURL: BASE, timeout: 45000 });

/**
 * Faults and shear zones for the current viewport, as GeoJSON LineStrings.
 *
 * Below roughly 1:250k the 100k layer is too dense to be readable and too big
 * to be polite, so we drop back to the 500k regional structures when zoomed out.
 */
export function queryWaFaults(bbox, { zoom = 12, maxResults = 1000 } = {}, signal) {
  const layer = zoom >= 10 ? WA_GEOLOGY_LAYERS.faults100k : WA_GEOLOGY_LAYERS.faults500k;
  return client.get(`/${layer}/query`, {
    signal,
    params: {
      geometry: bbox.join(','),
      geometryType: 'esriGeometryEnvelope',
      inSR: 4326,
      outSR: 4326,
      spatialRel: 'esriSpatialRelIntersects',
      outFields: 'feature,type,symbol',
      returnGeometry: true,
      resultRecordCount: maxResults,
      f: 'geojson',
    },
  });
}

/**
 * What rock am I standing on? Point identify against the interpreted bedrock
 * geology, preferring the 100k mapping and falling back to the 500k state layer
 * where 100k coverage doesn't exist.
 */
export async function identifyWaGeology(lngLat, signal) {
  const ask = (layer) =>
    client.get(`/${layer}/query`, {
      signal,
      params: {
        geometry: `${lngLat.lng},${lngLat.lat}`,
        geometryType: 'esriGeometryPoint',
        inSR: 4326,
        spatialRel: 'esriSpatialRelIntersects',
        outFields: '*',
        returnGeometry: false,
        f: 'json',
      },
    });

  const detailed = await ask(WA_GEOLOGY_LAYERS.bedrock100k);
  if (detailed.data?.features?.length) {
    return { scale: '1:100 000', attributes: detailed.data.features[0].attributes };
  }
  const regional = await ask(WA_GEOLOGY_LAYERS.bedrock500k);
  if (regional.data?.features?.length) {
    return { scale: '1:500 000', attributes: regional.data.features[0].attributes };
  }
  return null;
}

/**
 * Nearest mapped fault to a click, within `radiusM`. Lines almost never sit
 * exactly under the cursor, so a point identify has to search a small buffer.
 */
export async function identifyWaFault(lngLat, radiusM = 500, signal) {
  const res = await client.get(`/${WA_GEOLOGY_LAYERS.faults100k}/query`, {
    signal,
    params: {
      geometry: `${lngLat.lng},${lngLat.lat}`,
      geometryType: 'esriGeometryPoint',
      inSR: 4326,
      spatialRel: 'esriSpatialRelIntersects',
      distance: radiusM,
      units: 'esriSRUnit_Meter',
      outFields: 'feature,type,symbol,sourcedate',
      returnGeometry: false,
      resultRecordCount: 1,
      f: 'json',
    },
  });
  return res.data?.features?.[0]?.attributes ?? null;
}
